import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../core/auth/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { useToast } from '../../../core/notifications/ToastContext';
import AuthLayout from '../../../components/auth/AuthLayout';
import PasswordInput from '../../../components/auth/PasswordInput';
import { getErrorMessage } from '../../../core/utils/errorMessage';

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setname] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Public sign-up only creates workers. Other roles are assigned by a manager.
  const role = 'worker' as const;
  const [isLoading, setIsLoading] = useState(false);
  const [showDebugLogs, setShowDebugLogs] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [timeoutWarning, setTimeoutWarning] = useState(false);
  const { addToast } = useToast();
  const navigate = useNavigate();

  // Always call useAuth hook at the top level
  const { signUp, error: authError } = useAuth();

  // Add timer to show status messages during long operations
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let warningTimeoutId: NodeJS.Timeout;

    if (isLoading) {
      // Set messages based on elapsed time
      timeoutId = setTimeout(() => {
        setProcessingStatus('Still working...');

        // Set another timeout for warning
        warningTimeoutId = setTimeout(() => {
          setTimeoutWarning(true);
          setProcessingStatus('This is taking longer than expected. The server might be busy.');
        }, 8000); // Show warning after 8 seconds
      }, 3000); // Show first message after 3 seconds
    } else {
      setProcessingStatus('');
      setTimeoutWarning(false);
    }

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(warningTimeoutId);
    };
  }, [isLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if auth context is available
    if (!signUp) {
      addToast({
        type: 'error',
        title: 'Service Unavailable',
        message: 'Authentication service unavailable. Please reload the page.',
      });
      return;
    }

    setIsLoading(true);

    // Validate passwords match
    if (password !== confirmPassword) {
      addToast({
        type: 'error',
        title: 'Validation Error',
        message: 'Passwords do not match',
      });
      setIsLoading(false);
      return;
    }

    // Validate password length
    if (password.length < 6) {
      addToast({
        type: 'error',
        title: 'Validation Error',
        message: 'Password must be at least 6 characters',
      });
      setIsLoading(false);
      return;
    }

    try {
      await signUp(email, password, name, role);

      // No need to manually redirect, the AuthContext will handle it
      navigate('/auth/login?success=signup');
    } catch (err: unknown) {
      addToast({
        type: 'error',
        title: 'Registration Failed',
        message: getErrorMessage(err, 'Failed to register. Please try again.'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit}>
        {/* Auth errors (e.g. name already taken) render as an inline alert
            above the inputs — the form stays usable for another attempt */}
        {authError && (
          <div
            role="alert"
            className="mb-5 p-3 bg-red-500/20 text-red-300 dark:text-red-700 rounded-xl text-sm backdrop-blur-sm border border-red-400/30"
          >
            {authError}
          </div>
        )}
        <div className="mb-5">
          <input
            id="email"
            type="email"
            className="w-full px-5 py-4 text-base bg-white/10 border border-white/30 rounded-xl placeholder-white/60 text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 backdrop-blur-sm"
            placeholder="Email"
            aria-label="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="mb-5">
          <input
            id="name"
            type="text"
            className="w-full px-5 py-4 text-base bg-white/10 border border-white/30 rounded-xl placeholder-white/60 text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 backdrop-blur-sm"
            placeholder="Name"
            aria-label="Name"
            value={name}
            onChange={e => setname(e.target.value)}
            required
          />
        </div>

        <div className="mb-5">
          <PasswordInput
            id="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoComplete="new-password"
          />
        </div>

        <div className="mb-5">
          <PasswordInput
            id="confirmPassword"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
          />
        </div>

        <div className="mb-6">
          <select
            id="role"
            className="w-full px-5 py-4 text-base bg-white/10 border border-white/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 backdrop-blur-sm appearance-none"
            value={role}
            onChange={() => undefined}
            required
          >
            <option value="worker" className="bg-gray-800 dark:bg-gray-200 text-white">
              Worker
            </option>
          </select>
        </div>

        <button
          type="submit"
          className="w-full py-4 px-5 bg-white text-black hover:bg-white/90 dark:bg-white dark:text-black dark:hover:bg-white/90 text-base font-semibold rounded-xl transition duration-200 flex items-center justify-center min-h-[52px]"
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="flex items-center">
              <LoadingSpinner />
              <span className="ml-2">Creating Account...</span>
            </span>
          ) : (
            'Create account'
          )}
        </button>

        {/* Show processing status when operation takes a long time */}
        {processingStatus && (
          <div
            className={`mt-4 p-3 ${
              timeoutWarning
                ? 'bg-yellow-500/20 text-yellow-300 dark:text-yellow-700 border-yellow-400/30'
                : 'bg-blue-500/20 text-blue-300 dark:text-blue-700 border-blue-400/30'
            } rounded-xl text-sm backdrop-blur-sm border`}
          >
            {processingStatus}
            {timeoutWarning && (
              <div className="mt-2">
                <button
                  type="button"
                  className="text-xs underline focus:outline-none text-white/80 hover:text-white"
                  onClick={() => setShowDebugLogs(!showDebugLogs)}
                >
                  {showDebugLogs ? 'Hide' : 'Show'} Debug Logs
                </button>
              </div>
            )}
          </div>
        )}
      </form>

      <div className="mt-6">
        <Link
          to="/auth/login"
          className="text-base text-white/80 hover:text-white focus:outline-none transition duration-200"
        >
          Sign in
        </Link>
      </div>
    </AuthLayout>
  );
};

export default Register;
